import React, { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import SignaturePad from 'signature_pad';
import Modal from 'components/Modal';
import Button from 'components/Button';
import useWindowSize from 'hooks/useWindowSize';
import { SCREEN_SIZES } from 'style';
import { SignatureCanvas, Buttons } from './styled';

const PEN_COLOR = 'rgb(39, 60, 74)';

const SCALE = Math.ceil(window.devicePixelRatio);

const SignatureInputModal = ({
  onApply,
  onChange,
  onClose,
  signature,
  acceptButtonText = 'Submit',
  isOpen
}) => {
  const { width: windowWidth } = useWindowSize();
  const canvasWidth = windowWidth < SCREEN_SIZES.tabletPortrait ? 255 : 500;
  const canvasHeight = canvasWidth * 0.4;
  const [pad, setPad] = useState(null);

  const sigCanvas = useCallback(
    (node) => {
      if (node !== null) {
        setPad(
          new SignaturePad(node, {
            penColor: PEN_COLOR
          })
        );
        node.width = canvasWidth * SCALE;
        node.height = canvasHeight * SCALE;
        node.getContext('2d').scale(SCALE, SCALE);
      }
    },
    [canvasWidth, canvasHeight]
  );

  useEffect(() => {
    if (pad) {
      pad.onEnd = () => {
        const sig = pad.toDataURL('image/png');
        onChange(sig);
      };
    }

    return () => {
      if (pad) {
        pad.onEnd = null;
      }
    };
  }, [pad, onChange]);

  useLayoutEffect(() => {
    let blob;
    if (pad && !pad.isEmpty()) {
      blob = pad.toData();
    }
    if (pad) {
      pad.clear(); // otherwise isEmpty() might return incorrect value
      if (blob) {
        pad.fromData(blob);
      }
    }
  }, [pad]);

  const clearSignature = () => {
    pad.clear();
    onChange('');
  };

  const applySignature = () => {
    if (!pad.isEmpty()) {
      const sig = pad.toDataURL('image/png');
      onApply(sig);
    }
    onClose();
  };

  return (
    <Modal header="Sign below" onClose={onClose} isOpen={isOpen}>
      <SignatureCanvas>
        <canvas ref={sigCanvas} width={canvasWidth} height={canvasHeight} />
      </SignatureCanvas>
      <Buttons>
        <Button variant="dark" onClick={clearSignature}>
          Clear
        </Button>
        <Button disabled={!signature} onClick={applySignature} variant="primary">
          {acceptButtonText}
        </Button>
      </Buttons>
    </Modal>
  );
};

SignatureInputModal.propTypes = {
  onApply: PropTypes.func.isRequired,
  signature: PropTypes.node,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  acceptButtonText: PropTypes.string
};

const SignatureInput = ({ signature, onApply, variant, disabled, text, onChange }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        variant={variant}
        disabled={disabled}
        margin="0 0 1em 1em"
      >
        {text}
      </Button>

      <SignatureInputModal
        signature={signature}
        onClose={() => setShowModal(false)}
        onChange={onChange}
        onApply={onApply}
        isOpen={showModal}
      />
    </>
  );
};

export default SignatureInput;

SignatureInput.defaultProps = {
  disabled: false,
  text: 'Sign below'
};

SignatureInput.propTypes = {
  /* what happens when save button in modal is clicked */
  onApply: PropTypes.func.isRequired,
  variant: PropTypes.string,
  disabled: PropTypes.bool,
  text: PropTypes.string,
  signature: PropTypes.node,
  onChange: PropTypes.func
};
